import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  getChats,
  getOneTemplate,
  getWhatsappChatList,
  getWhatsappNumbers,
  getWhatsappTemplate,
  sendBulkMessage,
  sendTextMessage,
  updateIsSeen,
} from "../../api/WhatsappData.api";
import { IChat, IChatListItem, IWhatsappState } from "../../models/Chat.model";
import ResponseModel from "../../models/response.model";

// Interfaces
interface MessageByChatId {
  chatId: string;
  receiver: string;
  chats: IChat[];
  hasMore: boolean;
}

interface WhatsappState {
  whatsappData?: IWhatsappState;
  loading: boolean;
  chatListLoading?: boolean;
  templates?: any;
  phoneNumbers?: any;
  singleTemplate?: any;
  whatsappChatList?: IChatListItem[];
  chatBubbleLoading?: boolean;
  latestMessage?: IChat;
  selectedChatId?: string;
  messagesByChatId: Record<string, MessageByChatId>;
}

const initialState: WhatsappState = {
  loading: false,
  templates: [],
  singleTemplate: {},
  whatsappChatList: [],
  chatBubbleLoading: false,
  messagesByChatId: {},
};

// Async thunks
export const fetchWhatsappTemplates = createAsyncThunk(
  "whatsapp/fetchTemplates",
  async () => await getWhatsappTemplate(),
);
export const fetchWhatsappNumbers = createAsyncThunk<ResponseModel>(
  "whatsapp/fetchNumbers",
  async () => await getWhatsappNumbers(),
);

export const fetchWhatsappTemplateById = createAsyncThunk(
  "whatsapp/fetchTemplateById",
  async (id: string) => await getOneTemplate(id),
);

export const sendBulkWhatsappMessage = createAsyncThunk(
  "whatsapp/sendBulkMessage",
  async ({
    userPhoneNumber,
    templateName,
    languageCode,
    components,
  }: {
    userPhoneNumber: string;
    templateName: string;
    languageCode: string;
    components: any[];
  }) =>
    await sendBulkMessage({
      userPhoneNumber,
      templateName,
      languageCode,
      components,
    }),
);

export const fetchWhatsappChats = createAsyncThunk(
  "whatsapp/fetchChats",
  async ({ chatId, page }: { chatId: string; page: number }) =>
    await getChats(chatId, page),
);

export const fetchWhatsappChatsList = createAsyncThunk(
  "whatsapp/fetchChatList",
  async () => await getWhatsappChatList(),
);

export const sendTextMessageToNumber = createAsyncThunk(
  "whatsapp/sendTextMsg",
  async (formData: FormData, { rejectWithValue }) => {
    try {
      const response = await sendTextMessage(
        formData.get("sentTo") as string,
        formData.get("message") as string,
        Array.from(formData.getAll("attachments") as File[]),
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const updateChatIsSeenStatus = createAsyncThunk(
  "whatsapp/updateChatIsSeenStatus",
  async (chatId: string) => await updateIsSeen(chatId),
);

// Slice
const whatsappDataSlice = createSlice({
  name: "whatsappData",
  initialState,
  reducers: {
    updateChatListWithUnReadCount: (state, action) => {
      const { index, undReadCounts } = action.payload;
      if (state.whatsappChatList && state.whatsappChatList[index]) {
        state.whatsappChatList[index].unReadMessage = undReadCounts;
      }
    },

    setSelectedChatIdSlice: (state, action) => {
      state.selectedChatId = action.payload;
    },
    addMessageToChat: (state, action) => {
      const { chatId, message } = action.payload;
      if (!state.messagesByChatId[chatId]) {
        state.messagesByChatId[chatId] = {
          chatId,
          chats: [],
          receiver: "",
          hasMore: false,
        };
      }
      state.messagesByChatId[chatId].chats.push(message);
    },
    // In your whatsappDataSlice
    clearMessagesForChat: (state, action) => {
      const chatId = action.payload;
      if (state.messagesByChatId[chatId]) {
        state.messagesByChatId[chatId].chats = [];
      } else {
        state.messagesByChatId[chatId] = {
          chatId,
          chats: [],
          receiver: "",
          hasMore: false,
        };
      }
    },

    addNewMessageToChat: (state, action) => {
      const newMsg = action.payload;
      const existingChat = state.messagesByChatId[newMsg.chatId];
      if (existingChat) {
        existingChat.chats.push(newMsg);
      } else {
        state.messagesByChatId[newMsg.chatId] = {
          chatId: newMsg.chatId,
          receiver: newMsg.receiver || "",
          chats: [newMsg],
          hasMore: false,
        };
      }
    },
    setLatestMessage: (state, action) => {
      state.latestMessage = action.payload;
      const { chatId } = action.payload;

      if (chatId) {
        const chat = state.messagesByChatId[chatId];
        if (chat) {
          const exists = chat.chats.some(
            (msg) => msg._id === action.payload._id,
          );
          if (!exists) {
            chat.chats.push(action.payload);
          }
        }
      }

      state.whatsappChatList = [...(state.whatsappChatList ?? [])]
        .map((chat) => {
          if (chat?._id === chatId) {
            chat.latestMessage = action.payload;
          }
          return chat;
        })
        .sort((a, b) => {
          const dateA = new Date(a.latestMessage?.createdAt || 0).getTime();
          const dateB = new Date(b.latestMessage?.createdAt || 0).getTime();
          return dateB - dateA;
        });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWhatsappTemplates.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchWhatsappTemplates.fulfilled, (state, action) => {
        state.loading = false;
        state.templates = action.payload;
      })
      .addCase(fetchWhatsappTemplates.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchWhatsappNumbers.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchWhatsappNumbers.fulfilled, (state, action) => {
        state.loading = false;
        state.phoneNumbers = action.payload.data;
      })
      .addCase(fetchWhatsappNumbers.rejected, (state) => {
        state.loading = false;
      })

      .addCase(fetchWhatsappTemplateById.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchWhatsappTemplateById.fulfilled, (state, action) => {
        state.loading = false;
        state.singleTemplate = action.payload;
      })
      .addCase(fetchWhatsappTemplateById.rejected, (state) => {
        state.loading = false;
      })

      .addCase(sendBulkWhatsappMessage.pending, (state) => {
        state.loading = true;
      })
      .addCase(sendBulkWhatsappMessage.fulfilled, (state, action) => {
        state.loading = false;
        state.whatsappData = action.payload;
      })
      .addCase(sendBulkWhatsappMessage.rejected, (state) => {
        state.loading = false;
      })

      .addCase(fetchWhatsappChats.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchWhatsappChats.fulfilled, (state, action) => {
        state.loading = false;

        const newChatId = action?.payload._id;
        const newChats = action?.payload?.chats || [];
        const newReceiver = action?.payload?.receiver;
        const page = action?.meta?.arg?.page;

        if (page === 1) {
          state.messagesByChatId[newChatId] = {
            chatId: newChatId,
            receiver: newReceiver,
            chats: newChats,
            hasMore: action?.payload?.hasMore ?? false,
          };
        } else {
          const existing = state.messagesByChatId[newChatId];
          if (!existing || existing.chats.length === 0) {
            state.messagesByChatId[newChatId] = {
              chatId: newChatId,
              receiver: newReceiver,
              chats: newChats,
              hasMore: action?.payload?.hasMore ?? false,
            };
          } else {
            const existingIds = new Set(existing.chats.map((msg) => msg._id));
            const uniqueNewChats = newChats.filter(
              (msg: any) => !existingIds.has(msg._id),
            );

            state.messagesByChatId[newChatId] = {
              ...existing,
              chats: [...uniqueNewChats, ...existing.chats],
              hasMore: action?.payload?.hasMore ?? existing.hasMore,
            };
          }
        }
      })

      .addCase(fetchWhatsappChats.rejected, (state) => {
        state.loading = false;
      })

      // Update seen
      .addCase(updateChatIsSeenStatus.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateChatIsSeenStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.whatsappData = action.payload;
      })
      .addCase(updateChatIsSeenStatus.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchWhatsappChatsList.pending, (state) => {
        state.chatListLoading = true;
      })
      .addCase(fetchWhatsappChatsList.fulfilled, (state, action) => {
        state.chatListLoading = false;
        state.whatsappChatList = action.payload;
      })
      .addCase(fetchWhatsappChatsList.rejected, (state, action) => {
        state.chatListLoading = false;
      })
      .addCase(sendTextMessageToNumber.pending, (state) => {
        state.chatBubbleLoading = true;
      })
      .addCase(sendTextMessageToNumber.fulfilled, (state, action) => {
        state.chatBubbleLoading = false;
      });
  },
});

export const {
  setLatestMessage,
  setSelectedChatIdSlice,
  updateChatListWithUnReadCount,
  addMessageToChat,
  clearMessagesForChat,
  addNewMessageToChat,
} = whatsappDataSlice.actions;
export default whatsappDataSlice.reducer;
